import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["nip", "street", "city", "postalCode", "status"]

  // Convert UPPERCASE to Title Case, handling Polish characters
  titleCase(str) {
    if (!str) return str
    return str
      .toLowerCase()
      .split(' ')
      .map(word => {
        if (word.length === 0) return word
        // Handle words with slashes (e.g., "10/5")
        if (word.includes('/')) return word
        // Handle postal codes (e.g., "31-834")
        if (/^\d{2}-\d{3}$/.test(word)) return word
        return word.charAt(0).toUpperCase() + word.slice(1)
      })
      .join(' ')
  }

  async lookup() {
    const nip = this.nipTarget.value.replace(/[^0-9]/g, "")

    if (nip.length !== 10) {
      this.showStatus("error", this.element.dataset.nipLookupInvalidNipMessage || "NIP must be 10 digits")
      return
    }

    this.showStatus("loading", this.element.dataset.nipLookupLoadingMessage || "Looking up...")

    try {
      const today = new Date().toISOString().split('T')[0]
      const response = await fetch(`https://wl-api.mf.gov.pl/api/search/nip/${nip}?date=${today}`)

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const data = await response.json()

      if (data.result?.subject) {
        this.fillForm(data.result.subject)
        this.showStatus("success", this.element.dataset.nipLookupSuccessMessage || "Data loaded!")
      } else {
        this.showStatus("error", this.element.dataset.nipLookupNotFoundMessage || "Company not found")
      }
    } catch (error) {
      console.error("NIP lookup error:", error)
      this.showStatus("error", this.element.dataset.nipLookupErrorMessage || "Lookup failed")
    }
  }

  fillForm(subject) {
    // Parse address - format is typically "STREET, POSTAL CITY"
    const address = subject.residenceAddress || subject.workingAddress
    if (address) {
      const parts = address.split(", ")

      if (parts.length >= 2) {
        // Street is the first part
        if (this.hasStreetTarget) {
          this.streetTarget.value = this.titleCase(parts[0])
        }

        // Last part is "POSTAL CITY"
        const lastPart = parts[parts.length - 1]
        const postalMatch = lastPart.match(/^(\d{2}-\d{3})\s+(.+)$/)

        if (postalMatch) {
          if (this.hasPostalCodeTarget) {
            this.postalCodeTarget.value = postalMatch[1]
          }
          if (this.hasCityTarget) {
            this.cityTarget.value = this.titleCase(postalMatch[2])
          }
        } else {
          // Fallback: put the whole thing in city
          if (this.hasCityTarget) {
            this.cityTarget.value = this.titleCase(lastPart)
          }
        }
      }
    }
  }

  showStatus(type, message) {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = message
    this.statusTarget.classList.remove("hidden", "text-success", "text-error", "text-info")

    switch (type) {
      case "success":
        this.statusTarget.classList.add("text-success")
        setTimeout(() => this.statusTarget.classList.add("hidden"), 3000)
        break
      case "error":
        this.statusTarget.classList.add("text-error")
        break
      case "loading":
        this.statusTarget.classList.add("text-info")
        break
    }
  }
}
